function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { withRouter } from "react-router";
import { Link } from "react-router-dom";
import { CssContainer } from "../../components/container";
import PlunkDropDown from "../../components/plunk-dropdown";
import { Loader } from "../../components/loaders";
import Error from "../../components/error";
import account from "../../data/account";
import { hasTenantFeature } from "../../functions/tenant-features";
import { num, fetchSales, resolveBusinessModel } from "../sales/territory-common";
import PeopleShell, { PEOPLE_SUBTITLES } from "../people/shell";
import { getLifecycleOverview } from "../people/api";
import LifecycleConfigEditor from "./config-editor";

/*
 * Lifecycle (People redesign 2026-10-09, PlunkDocs mockups board "5 · Lifecycle").
 * Lead, Prospect, Customer as one funnel card with move rates between stages,
 * then "Ready to move on" and "Stuck" lists.
 *
 * Data: GET /v1/people/lifecycle?channel= -> { stages, moves, ready, stuck }.
 * While that endpoint is not deployed (null), fall back to the older calls:
 *   /v1/lifecycle/funnel/this_month  (totals + new this month)
 *   /v1/lifecycle/{stage}/this_year  (recent and long-waiting records)
 * The page shows no date range, so it can never disagree with the top-bar picker.
 * Stage rules are edited in LifecycleConfigEditor ("How stages work").
 *
 * Gated by the `lifecycle` registry feature.
 */

export const STAGES = [{
  key: "lead",
  label: "Leads",
  one: "Lead",
  desc: "Filled in a form or signed up for news"
}, {
  key: "prospect",
  label: "Prospects",
  one: "Prospect",
  desc: "Asked for a quote or created an account"
}, {
  key: "customer",
  label: "Customers",
  one: "Customer",
  desc: "Has ordered, or is on your customer list"
}];
const STAGE_ONE = {
  lead: "Lead",
  prospect: "Prospect",
  customer: "Customer"
};
export const CHANNELS = [["", "All channels"], ["b2b", "Business buyers"], ["b2c", "Shoppers"]];
function canEditConfig() {
  return account.inGroup("owner") || account.inGroup("administrator") || account.inGroup("root");
}
export function StageChip({
  stage
}) {
  const s = String(stage || "").toLowerCase();
  if (!STAGE_ONE[s]) return null;
  return /*#__PURE__*/React.createElement("span", {
    className: `pl-chip pl-chip-${s}`
  }, STAGE_ONE[s]);
}
function aboutDays(d) {
  if (d == null || isNaN(Number(d))) return null;
  const n = Math.max(1, Math.round(Number(d)));
  if (n < 14) return `in about ${n} day${n === 1 ? "" : "s"}`;
  if (n < 60) return `in about ${Math.round(n / 7)} weeks`;
  const m = Math.round(n / 30);
  return `in about ${m} month${m === 1 ? "" : "s"}`;
}
function shortDate(v) {
  if (!v) return "";
  const d = new Date(v);
  if (isNaN(d.getTime())) return String(v).slice(0, 10);
  return d.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric"
  });
}
function daysSince(v) {
  const d = new Date(v);
  if (!v || isNaN(d.getTime())) return null;
  return Math.floor((Date.now() - d.getTime()) / 86400000);
}
class LifecycleViewComponent extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onChannel", channel => {
      this.setState({
        channel,
        data: null,
        error: false
      });
      this.load(channel);
    });
    this.state = {
      data: null,
      error: false,
      channel: "",
      showConfig: false,
      businessModel: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load(this.state.channel);
    resolveBusinessModel().then(bm => {
      if (this._alive) this.setState({
        businessModel: bm
      });
    }).catch(() => {});
  }
  componentWillUnmount() {
    this._alive = false;
  }
  async load(channel) {
    let overview = null;
    try {
      overview = await getLifecycleOverview(channel ? {
        channel
      } : undefined);
    } catch (e) {
      overview = null;
    }
    if (overview && Array.isArray(overview.stages)) {
      if (this._alive) this.setState({
        data: this.fromOverview(overview),
        error: false
      });
      return;
    }
    try {
      const data = await this.loadFallback(channel);
      if (this._alive) this.setState({
        data,
        error: false
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }
  fromOverview(o) {
    const byStage = {};
    (o.stages || []).forEach(s => {
      byStage[String(s.stage || "").toLowerCase()] = s;
    });
    return {
      stages: STAGES.map(s => {
        const r = byStage[s.key] || {};
        return {
          ...s,
          total: r.total || 0,
          newThisMonth: r.newThisMonth || 0,
          desc: r.description || s.desc
        };
      }),
      moves: o.moves || [],
      ready: (o.ready || []).map(r => ({
        ...r,
        link: r.id ? `/Customers/account/${encodeURIComponent(String(r.id))}` : null
      })),
      stuck: (o.stuck || []).map(r => ({
        ...r,
        link: r.id ? `/Customers/account/${encodeURIComponent(String(r.id))}` : null
      }))
    };
  }
  async loadFallback(channel) {
    const chFirst = channel ? `?channel=${channel}` : "";
    const ch = channel ? `&channel=${channel}` : "";
    const [funnel, leads, prospects] = await Promise.all([fetchSales(`/lifecycle/funnel/this_month${chFirst}`), fetchSales(`/lifecycle/lead/this_year?top=100${ch}`).catch(() => []), fetchSales(`/lifecycle/prospect/this_year?top=100${ch}`).catch(() => [])]);
    const byStage = {};
    (funnel || []).forEach(f => {
      byStage[f.stage] = f;
    });
    const acct = r => r.customerId ? `/Customers/account/${r.customerId}` : null;
    const company = r => [r.region, (r.channel || "").toUpperCase()].filter(Boolean).join(", ");
    const byNewest = (a, b) => String(b.enteredDate || "").localeCompare(String(a.enteredDate || ""));
    const ready = [...(prospects || []).map(r => ({
      r,
      from: "prospect",
      to: "customer",
      reason: `Became a prospect on ${shortDate(r.enteredDate)}`
    })), ...(leads || []).map(r => ({
      r,
      from: "lead",
      to: "prospect",
      reason: `Became a lead on ${shortDate(r.enteredDate)}`
    }))].filter(x => {
      const d = daysSince(x.r.enteredDate);
      return d != null && d <= 14;
    }).sort((a, b) => byNewest(a.r, b.r)).slice(0, 5).map(x => ({
      id: x.r.customerId,
      name: x.r.name,
      company: company(x.r),
      from: x.from,
      to: x.to,
      reason: x.reason,
      link: acct(x.r)
    }));
    const stuck = [...(leads || []).map(r => ({
      r,
      stage: "lead"
    })), ...(prospects || []).map(r => ({
      r,
      stage: "prospect"
    }))].filter(x => {
      const d = daysSince(x.r.enteredDate);
      return d != null && d >= 45;
    }).sort((a, b) => byNewest(b.r, a.r)).slice(0, 5).map(x => ({
      id: x.r.customerId,
      name: x.r.name,
      company: company(x.r),
      stage: x.stage,
      reason: `A ${x.stage} since ${shortDate(x.r.enteredDate)}`,
      link: acct(x.r)
    }));
    return {
      stages: STAGES.map(s => {
        const f = byStage[s.key] || {};
        return {
          ...s,
          total: f.total != null ? f.total : f.count || 0,
          newThisMonth: f.count || 0
        };
      }),
      moves: [],
      ready,
      stuck,
      fallback: true
    };
  }
  customersLabel() {
    const bm = this.state.businessModel;
    return bm && bm !== "retail" ? "Dealers" : "Customers";
  }
  renderFunnel(data) {
    const max = Math.max(1, ...data.stages.map(s => Number(s.total) || 0));
    const moveFor = (from, to) => (data.moves || []).find(m => String(m.from).toLowerCase() === from && String(m.to).toLowerCase() === to);
    const out = [];
    data.stages.forEach((s, i) => {
      const t = Number(s.total) || 0;
      const pct = t > 0 ? Math.max(4, Math.round(Math.log(t + 1) / Math.log(max + 1) * 100)) : 0;
      out.push(/*#__PURE__*/React.createElement("div", {
        key: s.key,
        className: "pl-stage"
      }, /*#__PURE__*/React.createElement("div", {
        className: `pl-bar pl-bar-${s.key}`
      }, /*#__PURE__*/React.createElement("div", {
        style: {
          width: `${pct}%`
        }
      })), /*#__PURE__*/React.createElement("div", {
        className: "pl-stage-num"
      }, /*#__PURE__*/React.createElement("b", null, num(t)), s.newThisMonth > 0 ? /*#__PURE__*/React.createElement("span", {
        className: "pl-up"
      }, "+", num(s.newThisMonth), " this month") : null), /*#__PURE__*/React.createElement("b", {
        className: "pl-stage-name"
      }, s.label), /*#__PURE__*/React.createElement("span", {
        className: "pl-muted"
      }, s.desc)));
      const next = data.stages[i + 1];
      if (next) {
        const m = moveFor(s.key, next.key);
        const hasRate = m && m.rate != null && !isNaN(Number(m.rate));
        const when = m ? aboutDays(m.medianDays) : null;
        out.push(/*#__PURE__*/React.createElement("div", {
          key: `${s.key}-move`,
          className: "pl-move",
          "aria-hidden": !hasRate
        }, hasRate ? /*#__PURE__*/React.createElement("b", null, Math.round(Number(m.rate) * 100), "%") : null, hasRate ? /*#__PURE__*/React.createElement("span", {
          className: "pl-muted"
        }, "move on", when ? /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("br", null), when) : null) : null, /*#__PURE__*/React.createElement("span", {
          className: "pl-arrow"
        }, "\u2192")));
      }
    });
    return /*#__PURE__*/React.createElement("section", {
      className: "pl-card pl-funnel"
    }, out);
  }
  renderRow(r, right, key) {
    const body = /*#__PURE__*/React.createElement("div", {
      className: "pl-row-main"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pl-row-name"
    }, /*#__PURE__*/React.createElement("b", null, r.name || "Someone"), r.company ? /*#__PURE__*/React.createElement("span", {
      className: "pl-muted"
    }, " \xB7 ", r.company) : null), r.reason ? /*#__PURE__*/React.createElement("span", {
      className: "pl-row-reason"
    }, r.reason) : null);
    return /*#__PURE__*/React.createElement("div", {
      key: key,
      className: "pl-row"
    }, r.link ? /*#__PURE__*/React.createElement(Link, {
      className: "pl-row-link",
      to: r.link
    }, body) : body, right);
  }
  render() {
    if (!hasTenantFeature("lifecycle")) {
      return /*#__PURE__*/React.createElement(CssContainer, {
        cls: "margin-squeeze"
      }, /*#__PURE__*/React.createElement("p", {
        className: "module-empty"
      }, "Lifecycle is not turned on for this workspace yet. Contact Plunk to turn it on."));
    }
    const {
      data,
      error,
      channel,
      showConfig
    } = this.state;
    const customersLabel = this.customersLabel();
    const actions = /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "170px",
      value: channel,
      onChange: this.onChannel,
      options: CHANNELS
    }), canEditConfig() ? /*#__PURE__*/React.createElement("button", {
      className: "k-button k-button-md k-rounded-md k-button-solid k-button-solid-base",
      onClick: () => this.setState({
        showConfig: true
      })
    }, "How stages work") : null);
    let body;
    if (error) body = /*#__PURE__*/React.createElement(Error, null);else if (!data) body = /*#__PURE__*/React.createElement(Loader, null);else {
      body = /*#__PURE__*/React.createElement("div", {
        className: "pl-page"
      }, this.renderFunnel(data), /*#__PURE__*/React.createElement("div", {
        className: "pl-two"
      }, /*#__PURE__*/React.createElement("section", {
        className: "pl-card pl-list"
      }, /*#__PURE__*/React.createElement("div", {
        className: "pl-list-head"
      }, /*#__PURE__*/React.createElement("b", null, "Ready to move on"), /*#__PURE__*/React.createElement("span", {
        className: "pl-muted"
      }, "act now and they likely move up")), data.ready.length === 0 ? /*#__PURE__*/React.createElement("p", {
        className: "pl-empty"
      }, "Nobody stands out right now. When someone shows strong interest, they will show up here.") : data.ready.map((r, i) => this.renderRow(r, /*#__PURE__*/React.createElement("span", {
        className: "pl-row-move"
      }, /*#__PURE__*/React.createElement(StageChip, {
        stage: r.from
      }), /*#__PURE__*/React.createElement("span", {
        className: "pl-muted"
      }, "\u2192"), /*#__PURE__*/React.createElement(StageChip, {
        stage: r.to
      })), r.id || i))), /*#__PURE__*/React.createElement("section", {
        className: "pl-card pl-list"
      }, /*#__PURE__*/React.createElement("div", {
        className: "pl-list-head"
      }, /*#__PURE__*/React.createElement("b", null, "Stuck"), /*#__PURE__*/React.createElement("span", {
        className: "pl-muted"
      }, "longer than usual in their stage")), data.stuck.length === 0 ? /*#__PURE__*/React.createElement("p", {
        className: "pl-empty"
      }, "Nobody is stuck. Everyone is moving at their usual pace.") : data.stuck.map((r, i) => this.renderRow(r, /*#__PURE__*/React.createElement("span", {
        className: "pl-row-move"
      }, /*#__PURE__*/React.createElement(StageChip, {
        stage: r.stage
      }), r.email ? /*#__PURE__*/React.createElement("a", {
        className: "pl-link",
        href: `mailto:${r.email}`
      }, "Email") : r.link ? /*#__PURE__*/React.createElement(Link, {
        className: "pl-link",
        to: r.link
      }, "Open") : null), r.id || i)))), /*#__PURE__*/React.createElement("span", {
        className: "pl-foot"
      }, "These stages count accounts", customersLabel === "Dealers" ? " (dealers and companies)" : "", ", not people: the Contacts tab counts the people in them, so its numbers differ. ", customersLabel, " who stop ordering show under ", customersLabel, " as \u201CNeeds attention\u201D."));
    }

    // "How stages work" opens as a page inside the app frame (sidebar and top bar stay), not over the whole window.
    if (showConfig) {
      // Sub-page in the composer's format (white top bar, back arrow, title + one line), inside the app frame.
      return /*#__PURE__*/React.createElement("div", {
        className: "territory-view ec-view ecc-view pl-rules-page"
      }, /*#__PURE__*/React.createElement("div", {
        className: "ecc-top"
      }, /*#__PURE__*/React.createElement("button", {
        type: "button",
        className: "ecc-back",
        "aria-label": "Back to Lifecycle",
        onClick: () => this.setState({
          showConfig: false
        })
      }, /*#__PURE__*/React.createElement("svg", {
        width: "16",
        height: "16",
        viewBox: "0 0 16 16",
        "aria-hidden": "true"
      }, /*#__PURE__*/React.createElement("path", {
        d: "M10 3L5 8l5 5",
        fill: "none",
        stroke: "currentColor",
        strokeWidth: "1.8",
        strokeLinecap: "round",
        strokeLinejoin: "round"
      }))), /*#__PURE__*/React.createElement("div", {
        className: "ecc-titleblock"
      }, /*#__PURE__*/React.createElement("span", {
        className: "pl-sub-title"
      }, "How stages work"), /*#__PURE__*/React.createElement("span", {
        className: "ecc-meta"
      }, "The things that move someone from lead to prospect to customer")), /*#__PURE__*/React.createElement("span", {
        className: "ecc-spacer"
      })), /*#__PURE__*/React.createElement("div", {
        className: "pl-rules-wrap"
      }, /*#__PURE__*/React.createElement(LifecycleConfigEditor, {
        channel: channel,
        hideTitle: true,
        onClose: () => this.setState({
          showConfig: false
        })
      })));
    }
    return /*#__PURE__*/React.createElement(PeopleShell, {
      subtitle: PEOPLE_SUBTITLES.lifecycle,
      actions: actions,
      cls: "lifecycle-view pl-view",
      customersLabel: customersLabel
    }, body);
  }
}
export default withRouter(LifecycleViewComponent);